/**
 * Service bundles UI helpers (tracker item 85): paths, the visit chip's text
 * and colour, timeline wording and grouping. Pure functions.
 */
import * as types from '../../api/types';
import { formatJobDate } from '../../utils/jobPresentation';
import { jobStatusLabel } from '../../pages/jobUi';

export const RENEWALS_PATH = '/renewals';
export const BUNDLES_SETTINGS_PATH = '/settings/service-bundles';
export const BUNDLE_ALERTS_SETTINGS_PATH = '/settings/service-bundles/alerts';

export const enrollmentPath = (id: string) => `/enrollments/${encodeURIComponent(id)}`;
export const bundleEditPath = (id: string) => `${BUNDLES_SETTINGS_PATH}/${encodeURIComponent(id)}`;
export const BUNDLE_NEW_PATH = `${BUNDLES_SETTINGS_PATH}/new`;

/** What a chip needs; every job response carries it (null fields outside an enrollment). */
export interface EnrollmentRef {
  enrollmentId: string;
  enrollmentStep: number;
  enrollmentStepCount: number;
  bundleName: string;
}

/** The chip data of a job (any response shape carrying the fields), or null when it is not a bundle visit. */
export function enrollmentRefOf(job: types.JobEnrollmentRef | null | undefined): EnrollmentRef | null {
  if (!job || !job.enrollmentId || !job.enrollmentStep || !job.enrollmentStepCount) return null;
  return { enrollmentId: job.enrollmentId, enrollmentStep: job.enrollmentStep, enrollmentStepCount: job.enrollmentStepCount, bundleName: job.bundleName || 'Service bundle' };
}

/** `Spring Lawn Program · 2 of 3` */
export function chipLabel(ref: Pick<EnrollmentRef, 'bundleName' | 'enrollmentStep' | 'enrollmentStepCount'>): string {
  return `${ref.bundleName} · ${ref.enrollmentStep} of ${ref.enrollmentStepCount}`;
}

/** Plain text for paper and other places without colour: `Service bundle: Spring Lawn Program, visit 2 of 3`. */
export function chipPlainText(ref: Pick<EnrollmentRef, 'bundleName' | 'enrollmentStep' | 'enrollmentStepCount'>): string {
  return `Service bundle: ${ref.bundleName}, visit ${ref.enrollmentStep} of ${ref.enrollmentStepCount}`;
}

const CHIP_COLOURS = [
  'bg-blue-100 text-blue-900 ring-blue-300',
  'bg-emerald-100 text-emerald-900 ring-emerald-300',
  'bg-violet-100 text-violet-900 ring-violet-300',
  'bg-amber-100 text-amber-900 ring-amber-300',
  'bg-rose-100 text-rose-900 ring-rose-300',
  'bg-cyan-100 text-cyan-900 ring-cyan-300',
  'bg-lime-100 text-lime-900 ring-lime-300',
  'bg-fuchsia-100 text-fuchsia-900 ring-fuchsia-300',
];

/** A stable colour per enrollment: the same id always maps to the same palette entry. */
export function chipColourIndex(enrollmentId: string): number {
  let hash = 0;
  for (let i = 0; i < enrollmentId.length; i += 1) hash = (hash * 31 + enrollmentId.charCodeAt(i)) >>> 0;
  return hash % CHIP_COLOURS.length;
}

export function chipColourClasses(enrollmentId: string): string {
  return CHIP_COLOURS[chipColourIndex(enrollmentId)];
}

export const ENROLLMENT_STATUS_LABELS: Record<types.EnrollmentStatus, string> = {
  ACTIVE: 'Active',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
  PENDING_APPROVAL: 'Pending approval',
};

export function enrollmentStatusLabel(status: string): string {
  return ENROLLMENT_STATUS_LABELS[status as types.EnrollmentStatus] ?? jobStatusLabel(status);
}

export function enrollmentStatusClasses(status: string): string {
  switch (status) {
    case 'ACTIVE': return 'bg-green-100 text-green-800';
    case 'PENDING_APPROVAL': return 'bg-amber-100 text-amber-900';
    case 'CANCELLED': return 'bg-gray-200 text-gray-700';
    default: return 'bg-blue-100 text-blue-800';
  }
}

export const RENEWAL_STATUS_LABELS: Record<types.RenewalStatus, string> = {
  NONE: 'No renewal',
  PENDING_APPROVAL: 'Pending approval',
  ACTIVE: 'Renewed',
  COMPLETED: 'Renewed (done)',
};

/** Local calendar date (YYYY-MM-DD) of a Date. */
export function localDateString(now: Date = new Date()): string {
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

/** Whole days from `from` to `to` (YYYY-MM-DD); negative when `to` is earlier. */
export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to.slice(0, 10)}T00:00:00Z`) - Date.parse(`${from.slice(0, 10)}T00:00:00Z`)) / 86_400_000);
}

export function addDaysTo(date: string, days: number): string {
  return new Date(Date.parse(`${date.slice(0, 10)}T00:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10);
}

/** `Next visit in 38 days`, `Next visit tomorrow`, `Next visit today`, or null when there is none. */
export function nextVisitText(nextVisitDate: string | null | undefined, today: string = localDateString()): string | null {
  if (!nextVisitDate) return null;
  const days = daysBetween(today, nextVisitDate);
  if (days < 0) return `Next visit was due ${-days} day${-days === 1 ? '' : 's'} ago`;
  if (days === 0) return 'Next visit today';
  if (days === 1) return 'Next visit tomorrow';
  return `Next visit in ${days} days`;
}

/** `1 of 3 done` */
export function progressText(progress: types.EnrollmentProgress): string {
  return `${progress.completed} of ${progress.total} done${progress.cancelled ? `, ${progress.cancelled} cancelled` : ''}`;
}

/** Share of finished visits (completed or cancelled), 0 to 100. */
export function progressPercent(progress: types.EnrollmentProgress): number {
  return progress.total === 0 ? 0 : Math.round(((progress.completed + progress.cancelled) / progress.total) * 100);
}

/** The date one year after `date` (the default start of a renewal). */
export function plusOneYear(date: string): string {
  const d = new Date(`${date.slice(0, 10)}T00:00:00Z`);
  d.setUTCFullYear(d.getUTCFullYear() + 1);
  return d.toISOString().slice(0, 10);
}

/**
 * The auto-renew line of the enrollment header: `Renewed from 2026` for a renewal (the year of the
 * renewed season when known), `Renews 2027-03-01` while auto-renew is on, else `Does not auto-renew`.
 */
export function autoRenewNote(enrollment: Pick<types.BundleEnrollment, 'autoRenew' | 'startDate' | 'renewedFromId' | 'renewedToId' | 'status'>, renewedFromYear?: number | null): string {
  if (enrollment.renewedFromId) {
    const year = renewedFromYear ?? Number(enrollment.startDate.slice(0, 4)) - 1;
    return `Renewed from ${year}`;
  }
  if (enrollment.status === 'CANCELLED' || enrollment.status === 'PENDING_APPROVAL') return '';
  return enrollment.autoRenew ? `Renews ${plusOneYear(enrollment.startDate)}` : 'Does not auto-renew';
}

/** What a visit shows as its state: the job's status, or "Planned" before the job exists. */
export function stepStatusLabel(step: types.EnrollmentStep): string {
  return step.job ? jobStatusLabel(step.job.status) : 'Planned';
}

export function stepStatusTone(step: types.EnrollmentStep): 'done' | 'cancelled' | 'open' | 'planned' {
  const status = step.job?.status;
  if (!status) return 'planned';
  if (status === 'COMPLETED' || status === 'INVOICED') return 'done';
  if (status === 'CANCELLED') return 'cancelled';
  return 'open';
}

/** The date a visit shows: its job's scheduled date, else the planned date. */
export function stepDate(step: types.EnrollmentStep): string {
  return step.job?.scheduledDate ?? step.plannedDate;
}

/** The price a visit shows: the job's current unit price, else the price fixed at creation. */
export function stepUnitPrice(step: types.EnrollmentStep): string {
  return step.jobUnitPrice ?? step.unitPrice;
}

/** A visit price can be edited from the enrollment page while its job can still change. */
export function canEditStepPrice(step: types.EnrollmentStep): boolean {
  const status = step.job?.status;
  return Boolean(step.job) && status !== 'CANCELLED' && status !== 'INVOICED' && !step.job?.invoiceId;
}

export interface JobGroup<T> {
  /** The enrollment id, or '' for the jobs outside any enrollment */
  key: string;
  ref: EnrollmentRef | null;
  jobs: T[];
}

/**
 * Groups jobs by enrollment for the "group by enrollment" toggle. Groups keep the order of their first
 * job; the jobs of an enrollment are ordered by step; jobs outside any enrollment form one trailing group.
 */
export function groupJobsByEnrollment<T extends types.JobEnrollmentRef>(jobs: T[]): JobGroup<T>[] {
  const groups: JobGroup<T>[] = [];
  const index = new Map<string, JobGroup<T>>();
  const loose: JobGroup<T> = { key: '', ref: null, jobs: [] };
  jobs.forEach((job) => {
    const ref = enrollmentRefOf(job);
    if (!ref) { loose.jobs.push(job); return; }
    let group = index.get(ref.enrollmentId);
    if (!group) {
      group = { key: ref.enrollmentId, ref, jobs: [] };
      index.set(ref.enrollmentId, group);
      groups.push(group);
    }
    group.jobs.push(job);
  });
  groups.forEach((group) => group.jobs.sort((a, b) => (a.enrollmentStep ?? 0) - (b.enrollmentStep ?? 0)));
  return loose.jobs.length ? [...groups, loose] : groups;
}

/** Programme total of a preview or enrollment for list cards: "3 visits over 90 days". */
export function spanText(count: number, spanDays: number): string {
  return `${count} visit${count === 1 ? '' : 's'}${count > 1 ? ` over ${spanDays} day${spanDays === 1 ? '' : 's'}` : ''}`;
}

/** The planned dates of a draft bundle from its components' gaps (the first falls on day 0). */
export function timelineOffsets(components: { daysAfterPrevious: number }[]): number[] {
  let total = 0;
  return components.map((c, index) => {
    if (index > 0) total += Math.max(0, Number.isFinite(c.daysAfterPrevious) ? c.daysAfterPrevious : 0);
    return total;
  });
}

export function dateLabel(date: string | null | undefined): string {
  return formatJobDate(date);
}

/** Filename-safe CSV save, same as other exports. */
export { saveBlob } from '../../pages/settings/importExportUi';
